import { router } from 'expo-router';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import Svg, {
  Circle,
  Defs,
  G,
  LinearGradient,
  Path,
  Rect,
  Stop,
} from 'react-native-svg';

import { Button } from '@/src/components/Button';
import { brandColors, colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

type InternalNotFoundProps = {
  eyebrow?: string;
  message?: string;
  title?: string;
};

function FrienzzoneLostGraphic() {
  return (
    <View style={styles.graphicWrap} accessibilityElementsHidden>
      <Svg width="100%" height="100%" viewBox="0 0 280 210">
        <Defs>
          <LinearGradient id="sky" x1="24" y1="16" x2="250" y2="190">
            <Stop offset="0" stopColor="#fff7fc" />
            <Stop offset="0.52" stopColor="#edf7ff" />
            <Stop offset="1" stopColor="#f4edff" />
          </LinearGradient>
          <LinearGradient id="pin" x1="102" y1="42" x2="174" y2="128">
            <Stop offset="0" stopColor={brandColors.magenta} />
            <Stop offset="0.55" stopColor={brandColors.purple} />
            <Stop offset="1" stopColor={brandColors.blue} />
          </LinearGradient>
          <LinearGradient id="trail" x1="44" y1="159" x2="230" y2="159">
            <Stop offset="0" stopColor={brandColors.magenta} />
            <Stop offset="0.42" stopColor={brandColors.purple} />
            <Stop offset="0.72" stopColor={brandColors.blue} />
            <Stop offset="1" stopColor={brandColors.orange} />
          </LinearGradient>
        </Defs>
        <Rect
          x="18"
          y="16"
          width="244"
          height="178"
          rx="34"
          fill="url(#sky)"
          stroke="#ebeaea"
          strokeWidth="2"
        />
        <Path
          d="M53 151 C82 118 102 179 133 145 S193 107 224 139"
          fill="none"
          stroke="url(#trail)"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray="1 20"
        />
        <Circle cx="72" cy="73" r="16" fill="#ffffff" stroke="#ebeaea" />
        <Circle cx="213" cy="77" r="20" fill="#ffffff" stroke="#ebeaea" />
        <Circle cx="202" cy="142" r="12" fill="#ffffff" stroke="#ebeaea" />
        <G opacity="0.95">
          <Path
            d="M140 36 C108 36 82 61 82 92 C82 134 140 172 140 172 C140 172 198 134 198 92 C198 61 172 36 140 36 Z"
            fill="url(#pin)"
          />
          <Circle cx="140" cy="91" r="42" fill="#ffffff" opacity="0.94" />
          <Path
            d="M116 94 C116 83 124 75 134 75 C139 75 144 78 147 83 C150 78 155 75 160 75 C170 75 178 83 178 94 C178 113 147 129 147 129 C147 129 116 113 116 94 Z"
            fill="none"
            stroke={brandColors.magenta}
            strokeWidth="8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <Circle cx="127" cy="94" r="5" fill={brandColors.purple} />
          <Circle cx="147" cy="83" r="5" fill={brandColors.blue} />
          <Circle cx="167" cy="94" r="5" fill={brandColors.purple} />
          <Path
            d="M127 94 L147 83 L167 94"
            fill="none"
            stroke={brandColors.blue}
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </G>
        <Path
          d="M214 43 L219 54 L231 55 L222 63 L225 75 L214 69 L203 75 L206 63 L197 55 L209 54 Z"
          fill={brandColors.warm}
        />
        <Circle cx="59" cy="118" r="7" fill={brandColors.magenta} />
        <Circle cx="230" cy="126" r="7" fill={brandColors.sky} />
      </Svg>
    </View>
  );
}

export function InternalNotFound({
  eyebrow = '404',
  message = 'This link may be old, private, or no longer available.',
  title = 'Lost in the Frienzzone',
}: InternalNotFoundProps) {
  const canGoBack = router.canGoBack();

  return (
    <View style={styles.container}>
      <FrienzzoneLostGraphic />
      <View style={styles.copy}>
        <Text style={styles.eyebrow}>{eyebrow}</Text>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.message}>{message}</Text>
      </View>
      <View style={styles.actions}>
        <Button
          title="Back to Home"
          style={styles.actionButton}
          onPress={() => router.replace('/home')}
        />
        {canGoBack ? (
          <Pressable
            accessibilityRole="button"
            onPress={() => router.back()}
            style={({ pressed }) => [
              styles.textButton,
              pressed ? styles.textButtonPressed : null,
            ]}
          >
            <Text style={styles.textButtonLabel}>Go back</Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  actionButton: {
    minWidth: 180,
  },
  actions: {
    alignItems: 'center',
    gap: spacing.md,
    width: '100%',
  },
  container: {
    alignItems: 'center',
    alignSelf: 'center',
    flex: 1,
    gap: spacing.lg,
    justifyContent: 'center',
    maxWidth: 520,
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.xxl,
    width: '100%',
  },
  copy: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  eyebrow: {
    color: colors.primary,
    fontSize: 13,
    fontWeight: '900',
    letterSpacing: 0,
    textTransform: 'uppercase',
  },
  graphicWrap: {
    aspectRatio: 4 / 3,
    maxWidth: 320,
    width: '78%',
  },
  message: {
    color: colors.mutedText,
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 23,
    maxWidth: 380,
    textAlign: 'center',
  },
  textButton: {
    borderRadius: 8,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  textButtonLabel: {
    color: colors.secondary,
    fontSize: 15,
    fontWeight: '900',
  },
  textButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  title: {
    color: colors.text,
    fontSize: 30,
    fontWeight: '900',
    letterSpacing: 0,
    lineHeight: 36,
    textAlign: 'center',
  },
});
